// The image editor's model and renderer, like the app's EditState/ImageRenderer
// (app/.../media/edit): everything in coordinates 0..1 of the oriented picture, so it is
// independent of the preview size; rendered orient → hide → colour → marks → crop.

export type Look = "none" | "mono" | "warm" | "cool";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type Mark =
  | { kind: "stroke"; color: string; size: number; highlighter: boolean; points: [number, number][] }
  | { kind: "shape"; shape: "arrow" | "rect" | "ellipse"; color: string; size: number; from: [number, number]; to: [number, number] }
  | { kind: "label"; text: string; color: string; size: number; at: [number, number]; boxed: boolean }
  | { kind: "redact"; mode: "pixelate" | "black"; rect: Rect };

export interface EditState {
  rotation: 0 | 90 | 180 | 270;
  flipped: boolean;
  crop: Rect;
  marks: Mark[];
  brightness: number;
  contrast: number;
  saturation: number;
  look: Look;
}

export const INITIAL: EditState = { rotation: 0, flipped: false, crop: { x: 0, y: 0, w: 1, h: 1 }, marks: [], brightness: 0, contrast: 0, saturation: 0, look: "none" };

/** White, black, red, orange, yellow, green, blue, purple (app: PEN_COLORS). */
export const PEN_COLORS = ["#ffffff", "#000000", "#e03131", "#f08c00", "#fcc419", "#2f9e44", "#1971c2", "#9c36b5"];
/** Stroke widths as a share of the picture's long edge. */
export const SIZES = [0.004, 0.008, 0.016];

type Matrix = number[];
const IDENTITY: Matrix = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];

function concat(a: Matrix, b: Matrix): Matrix {
  // a ∘ b for 4×5 colour matrices (rows r, g, b, a; last column the offset).
  const out = new Array<number>(20);
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 5; c++) {
      let v = c === 4 ? a[r * 5 + 4]! : 0;
      for (let k = 0; k < 4; k++) v += a[r * 5 + k]! * (c === 4 ? b[k * 5 + 4]! : b[k * 5 + c]!);
      out[r * 5 + c] = v;
    }
  }
  return out;
}

const scale = (r: number, g: number, b: number): Matrix => [r, 0, 0, 0, 0, 0, g, 0, 0, 0, 0, 0, b, 0, 0, 0, 0, 0, 1, 0];

function saturation(s: number): Matrix {
  const r = 0.213 * (1 - s);
  const g = 0.715 * (1 - s);
  const b = 0.072 * (1 - s);
  return [r + s, g, b, 0, 0, r, g + s, b, 0, 0, r, g, b + s, 0, 0, 0, 0, 0, 1, 0];
}

/** The colour matrix of the adjustments (Adjust.matrix in the app). */
export function colorMatrix(s: Pick<EditState, "brightness" | "contrast" | "saturation" | "look">): Matrix | null {
  if (!s.brightness && !s.contrast && !s.saturation && s.look === "none") return null;
  let m = IDENTITY;
  if (s.look === "mono") m = concat(saturation(0), m);
  if (s.look === "warm") m = concat(scale(1.12, 1.02, 0.88), m);
  if (s.look === "cool") m = concat(scale(0.9, 1, 1.12), m);
  if (s.saturation) m = concat(saturation(1 + s.saturation), m);
  if (s.contrast || s.brightness) {
    const k = 1 + s.contrast;
    const shift = 128 * (1 - k) + s.brightness * 100;
    m = concat([k, 0, 0, 0, shift, 0, k, 0, 0, shift, 0, 0, k, 0, shift, 0, 0, 0, 1, 0], m);
  }
  return m;
}

export function applyMatrix(data: Uint8ClampedArray, m: Matrix) {
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;
    const a = data[i + 3]!;
    data[i] = m[0]! * r + m[1]! * g + m[2]! * b + m[3]! * a + m[4]!;
    data[i + 1] = m[5]! * r + m[6]! * g + m[7]! * b + m[8]! * a + m[9]!;
    data[i + 2] = m[10]! * r + m[11]! * g + m[12]! * b + m[13]! * a + m[14]!;
  }
}

/** The picture with every edit but the crop, as large as [maxEdge] allows. */
export function renderFull(source: CanvasImageSource & { width: number; height: number }, s: EditState, maxEdge: number, skipMarks = false): HTMLCanvasElement {
  const quarter = s.rotation === 90 || s.rotation === 270;
  const sw = source.width;
  const sh = source.height;
  const k = Math.min(1, maxEdge / Math.max(sw, sh));
  const w = Math.round((quarter ? sh : sw) * k);
  const h = Math.round((quarter ? sw : sh) * k);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.save();
  ctx.translate(w / 2, h / 2);
  // Mirrored after the turn, so "Spiegeln" is always left to right as seen (see geometry.ts).
  if (s.flipped) ctx.scale(-1, 1);
  ctx.rotate((s.rotation * Math.PI) / 180);
  ctx.drawImage(source, (-sw * k) / 2, (-sh * k) / 2, sw * k, sh * k);
  ctx.restore();
  for (const m of s.marks) if (m.kind === "redact") redact(ctx, m, w, h);
  const matrix = colorMatrix(s);
  if (matrix) {
    const img = ctx.getImageData(0, 0, w, h);
    applyMatrix(img.data, matrix);
    ctx.putImageData(img, 0, 0);
  }
  if (!skipMarks) for (const m of s.marks) drawMark(ctx, m, w, h);
  return canvas;
}

/** The finished picture: everything, cropped. */
export function render(source: CanvasImageSource & { width: number; height: number }, s: EditState, maxEdge = 2560): HTMLCanvasElement {
  const full = renderFull(source, s, maxEdge);
  const c = s.crop;
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(full.width * c.w));
  out.height = Math.max(1, Math.round(full.height * c.h));
  out.getContext("2d")!.drawImage(full, full.width * c.x, full.height * c.y, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

function redact(ctx: CanvasRenderingContext2D, m: Extract<Mark, { kind: "redact" }>, w: number, h: number) {
  const x = Math.round(m.rect.x * w);
  const y = Math.round(m.rect.y * h);
  const rw = Math.max(1, Math.round(m.rect.w * w));
  const rh = Math.max(1, Math.round(m.rect.h * h));
  if (m.mode === "black") {
    ctx.fillStyle = "#000";
    ctx.fillRect(x, y, rw, rh);
    return;
  }
  // Real block averages, so nothing can be read back (app: block = max(16 px, long edge / 40)).
  const block = Math.max(16, Math.round(Math.max(w, h) / 40));
  const img = ctx.getImageData(x, y, rw, rh);
  const d = img.data;
  for (let by = 0; by < rh; by += block) {
    for (let bx = 0; bx < rw; bx += block) {
      let r = 0, g = 0, b = 0, n = 0;
      for (let yy = by; yy < Math.min(by + block, rh); yy++) for (let xx = bx; xx < Math.min(bx + block, rw); xx++) {
        const i = (yy * rw + xx) * 4;
        r += d[i]!; g += d[i + 1]!; b += d[i + 2]!; n++;
      }
      for (let yy = by; yy < Math.min(by + block, rh); yy++) for (let xx = bx; xx < Math.min(bx + block, rw); xx++) {
        const i = (yy * rw + xx) * 4;
        d[i] = r / n; d[i + 1] = g / n; d[i + 2] = b / n;
      }
    }
  }
  ctx.putImageData(img, x, y);
}

export function drawMark(ctx: CanvasRenderingContext2D, m: Mark, w: number, h: number) {
  const long = Math.max(w, h);
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (m.kind === "stroke") {
    ctx.strokeStyle = m.color;
    ctx.globalAlpha = m.highlighter ? 0.4 : 1;
    ctx.lineWidth = m.size * long * (m.highlighter ? 3 : 1);
    ctx.beginPath();
    m.points.forEach(([x, y], i) => (i ? ctx.lineTo(x * w, y * h) : ctx.moveTo(x * w, y * h)));
    if (m.points.length === 1) ctx.lineTo(m.points[0]![0] * w + 0.1, m.points[0]![1] * h);
    ctx.stroke();
  } else if (m.kind === "shape") {
    ctx.strokeStyle = m.color;
    ctx.lineWidth = m.size * long;
    const [x1, y1] = [m.from[0] * w, m.from[1] * h];
    const [x2, y2] = [m.to[0] * w, m.to[1] * h];
    ctx.beginPath();
    if (m.shape === "rect") ctx.rect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    else if (m.shape === "ellipse") ctx.ellipse((x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2, 0, 0, Math.PI * 2);
    else {
      const a = Math.atan2(y2 - y1, x2 - x1);
      const head = Math.max(12, m.size * long * 4);
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.moveTo(x2 - head * Math.cos(a - 0.5), y2 - head * Math.sin(a - 0.5));
      ctx.lineTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(a + 0.5), y2 - head * Math.sin(a + 0.5));
    }
    ctx.stroke();
  } else if (m.kind === "label") {
    const size = m.size * long * 4;
    ctx.font = `600 ${size}px Roboto, sans-serif`;
    ctx.textBaseline = "middle";
    const x = m.at[0] * w;
    const y = m.at[1] * h;
    if (m.boxed) {
      const tw = ctx.measureText(m.text).width;
      ctx.fillStyle = m.color === "#ffffff" ? "#000000" : "#ffffff";
      ctx.globalAlpha = 0.85;
      ctx.fillRect(x - size * 0.3, y - size * 0.7, tw + size * 0.6, size * 1.4);
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = m.color;
    ctx.fillText(m.text, x, y);
  }
  ctx.restore();
}
